// Import Libraries
import React, { useState, useContext, useEffect } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

// Import Services
import subscriptionService from '../../../services/subscriptionService';

// Context
import { UserContext } from '../../../context/User';

const STEP = {
  CONFIRM: 1,
  REASON: 2,
  FEEDBACK: 3,
};

const CANCEL_REASONS = [
  "It's too expensive",
  "I'm not using it enough",
  'No upcoming projects',
  'Poor quality or performance',
  'Missing features I need',
  "It's too complicated to use",
  'Switching to a competitor',
  'Other reason',
];

const MAX_COMMENT_LENGTH = 500;

const ConfirmCancelSubscription = ({ currentSubscription, isOpen, setIsOpen, onRefresh }) => {
  const { user } = useContext(UserContext);

  const [step, setStep] = useState(STEP.CONFIRM);

  // step 2 + 3 state
  const [selectedReason, setSelectedReason] = useState('');
  const [comment, setComment] = useState('');

  // error state (existing behavior)
  const [message, setMessage] = useState(null);

  const endDate = currentSubscription?.end_date || currentSubscription?.next_recurring;

  useEffect(() => {
    if (!isOpen) return;

    // reset flow each time modal opens
    setStep(STEP.CONFIRM);
    setSelectedReason('');
    setComment('');
    setMessage(null);
  }, [isOpen]);

  const closeModal = () => setIsOpen(false);

  const formatLongDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Step 1 -> Step 2 (no cancellation yet)
  const goToReasonStep = () => {
    setMessage(null);
    setStep(STEP.REASON);
  };

  // Step 2 -> Step 3 (pick reason and advance)
  const onSelectReason = (reason) => {
    setSelectedReason(reason);

    setStep(STEP.FEEDBACK);
  };

  const requiresComment = selectedReason === 'Other reason';

  const canCompleteCancellation =
    Boolean(selectedReason) && (!requiresComment || comment.trim().length > 0);

  // Step 3 -> cancel subscription
  const onCompleteCancellation = async () => {
    // (4) Defensive guard (textarea maxLength should already prevent this)
    if (comment.length > MAX_COMMENT_LENGTH) {
      toast.error('Feedback must be 500 characters or less.');
      return;
    }

    // Disable “Complete Cancellation” until a reason is selected
    if (!selectedReason) {
      toast.error('Please select a reason for cancelling.');
      return;
    }

    // Make “Other reason” require a comment
    if (requiresComment && comment.trim().length === 0) {
      toast.error('Please add a brief comment for "Other reason".');
      return;
    }

    setMessage(false);

    try {
      const cancelSubscription = await subscriptionService.cancelSubscription(
        currentSubscription.id,
        user.id,
        {
          reason: selectedReason,
          comment,
        },
      );

      if (cancelSubscription) {
        toast.success(
          `Your subscription has been canceled successfully. You'll still have access to all Pro features until ${endDate}.`,
        );
        if (onRefresh) onRefresh();
        setIsOpen(false);
      } else {
        toast.error(
          `There was a problem cancelling your subscription. Please try again later or contact us.`,
        );
        setMessage(true);
      }
    } catch (e) {
      toast.error(`There was a problem cancelling your subscription. Please try again later.`);
      setMessage(true);
    }
  };

  const commonHeaderStyles = {
    textAlign: 'center',
    paddingTop: '24px',
    paddingBottom: '8px',
  };

  const modalContentStyles = { padding: '16px 32px 32px' };

  // Shared “card” styles used in steps 2/3
  const optionCardStyle = (isSelected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    borderRadius: '14px',
    border: isSelected ? '2px solid #7A64EB' : '1px solid #E7E7EA',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    userSelect: 'none',
    marginBottom: '12px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  });

  const radioOuterStyle = (isSelected) => ({
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: isSelected ? '2px solid #7A64EB' : '2px solid #BDBDC7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 18px',
  });

  const radioInnerStyle = (isSelected) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: isSelected ? '#7A64EB' : 'transparent',
  });

  const renderStep1 = () => (
    <>
      <div className="close-button" role="button" onClick={closeModal}>
        <FontAwesomeIcon icon={faTimes} />
      </div>

      <div className="modalHeader" style={commonHeaderStyles}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>😢</div>
        <div className="bold DeepPadded" style={{ fontSize: '22px' }}>
          Are you sure you want to cancel?
        </div>
      </div>

      <div className="modalContent" style={modalContentStyles}>
        <div className="MidPadded">
          <span role="img" aria-label="sparkles">
            ✨
          </span>{' '}
          You will lose these time saving benefits
        </div>

        <div className="MidPadded">
          {[
            'The ability to generate custom shotlists for up to 3 scripts per month',
            'Export to CSV that makes it easy to work offline',
            'The ability to collaborate with your team',
            'Premier customer support',
          ].map((item) => (
            <div
              key={item}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '10px',
              }}
            >
              <FontAwesomeIcon
                icon={faCheckCircle}
                style={{ marginTop: '2px', color: '#7A64EB', fontSize: '14px' }}
              />
              <span style={{ fontSize: '14px', color: '#333' }}>{item}</span>
            </div>
          ))}
        </div>

        {/* Yellow info card */}
        <div
          style={{
            backgroundColor: '#FFF5D6',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '12px',
            fontSize: '14px',
            color: '#4A3A00',
          }}
        >
          <div>
            Your subscription will stay active until the last day of your current enrollment.
          </div>
          {endDate && (
            <div style={{ marginTop: '4px', fontWeight: 600 }}>{formatLongDate(endDate)}</div>
          )}
        </div>

        {/* Gray/blue info card */}
        <div
          style={{
            backgroundColor: '#F2F3FF',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '24px',
            fontSize: '14px',
            color: '#222',
          }}
        >
          After cancelation the existing projects will remain accessible, but exports will be
          restricted to PDF.
        </div>

        {/* Buttons */}
        <div
          className="FullWidthFlex"
          style={{
            display: 'flex',
            gap: '16px',
            marginTop: '8px',
          }}
        >
          <button
            className="TwoPortionFlex buttonClone"
            id="cancel_subscription"
            onClick={goToReasonStep}
            style={{
              border: '1px solid #D32F2F',
              backgroundColor: '#FFFFFF',
              color: '#D32F2F',
            }}
          >
            Cancel my Subscription
          </button>

          <button className="TwoPortionFlex buttonClone" onClick={closeModal}>
            Nevermind, keep my subscription
          </button>
        </div>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="close-button" role="button" onClick={closeModal}>
        <FontAwesomeIcon icon={faTimes} />
      </div>

      <div className="modalHeader" style={{ ...commonHeaderStyles, paddingBottom: '0px' }}>
        <div className="bold" style={{ fontSize: '26px', marginBottom: '8px' }}>
          We&apos;re sorry to see you go
        </div>
        <div style={{ fontSize: '16px', padding: '20px', fontWeight: 400 }}>
          Help us improve by letting us know why you&apos;re cancelling
        </div>
      </div>

      <div className="modalContent" style={{ ...modalContentStyles, paddingTop: '18px' }}>
        {/* Scrollable list with bottom gradient fade */}
        <div style={{ position: 'relative' }}>
          <div
            style={{
              maxHeight: '270px',
              overflowY: 'auto',
              paddingRight: '6px',
              paddingBottom: '18px',
            }}
          >
            {CANCEL_REASONS.map((reason) => {
              const isSelected = selectedReason === reason;
              return (
                <div
                  key={reason}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectReason(reason)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onSelectReason(reason);
                  }}
                  style={optionCardStyle(isSelected)}
                >
                  <div style={radioOuterStyle(isSelected)}>
                    <div style={radioInnerStyle(isSelected)} />
                  </div>
                  <div style={{ fontSize: '15px', color: '#1E1E22' }}>{reason}</div>
                </div>
              );
            })}
          </div>

          {/* Gradient fade overlay */}
          <div
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: '46px',
              background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
              borderRadius: '14px',
            }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '14px' }}>
          <button
            className="buttonClone"
            onClick={() => setStep(STEP.CONFIRM)}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#7A64EB',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Back
          </button>
        </div>
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <div className="close-button" role="button" onClick={closeModal}>
        <FontAwesomeIcon icon={faTimes} />
      </div>

      <div className="modalHeader" style={{ ...commonHeaderStyles, paddingBottom: '0px' }}>
        <div className="bold" style={{ fontSize: '26px', marginBottom: '8px' }}>
          Help us improve
        </div>
        <div style={{ fontSize: '16px', padding: '20px', fontWeight: 400 }}>
          Your detailed feedback is invaluable to us
        </div>
      </div>

      <div className="modalContent" style={{ ...modalContentStyles, paddingTop: '18px' }}>
        {!!selectedReason && (
          <div
            style={{
              backgroundColor: '#F6F6FA',
              border: '1px solid #E7E7EA',
              borderRadius: '12px',
              padding: '10px 12px',
              fontSize: '13px',
              color: '#2A2A30',
              marginBottom: '12px',
            }}
          >
            <span style={{ color: '#6B6B75' }}>Reason: </span>
            <span style={{ fontWeight: 600 }}>{selectedReason}</span>
          </div>
        )}

        <textarea
          value={comment}
          maxLength={MAX_COMMENT_LENGTH} // (1) hard limit input
          onChange={(e) => setComment(e.target.value)}
          placeholder={
            requiresComment
              ? 'Please tell us why you are cancelling...'
              : 'What could we have done better?'
          }
          style={{
            width: '100%',
            minHeight: '120px',
            resize: 'vertical',
            borderRadius: '12px',
            border:
              requiresComment && comment.trim().length === 0
                ? '1px solid #D32F2F'
                : '1px solid #E7E7EA',
            padding: '12px 14px',
            fontSize: '14px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />

        {/* (3) character counter */}
        <div
          style={{
            marginTop: '6px',
            fontSize: '12px',
            color: comment.length >= MAX_COMMENT_LENGTH ? '#D32F2F' : '#6B6B75',
            textAlign: 'right',
          }}
        >
          {MAX_COMMENT_LENGTH - comment.length} characters remaining
        </div>

        <button
          className="buttonClone"
          id="complete_cancellation"
          onClick={onCompleteCancellation}
          disabled={!canCompleteCancellation} // Disable “Complete Cancellation” until a reason is selected, and enforce comment for Other
          style={{
            width: '100%',
            marginTop: '16px',
            padding: '14px 16px',
            borderRadius: '12px',
            border: 'none',
            backgroundColor: canCompleteCancellation ? '#7A64EB' : '#B9B2F3',
            color: '#FFFFFF',
            fontSize: '15px',
            cursor: canCompleteCancellation ? 'pointer' : 'not-allowed',
          }}
        >
          Complete Cancellation
        </button>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
          <button
            className="buttonClone"
            onClick={() => setStep(STEP.REASON)}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#7A64EB',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Back
          </button>
        </div>

        {message && (
          <div
            id="payment-message"
            className="fullWidthContainer padded"
            style={{ marginTop: '16px', fontSize: '13px' }}
          >
            There was an error cancelling your subscription. Please try again later or{' '}
            <a href="https://www.shotkraft.com/contact-us">contact us</a>.
          </div>
        )}
      </div>
    </>
  );

  return (
    <Modal isOpen={isOpen} contentLabel="Cancel Subscription" className="modal wide">
      {step === STEP.CONFIRM && renderStep1()}
      {step === STEP.REASON && renderStep2()}
      {step === STEP.FEEDBACK && renderStep3()}
    </Modal>
  );
};

export default ConfirmCancelSubscription;
